import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { SETUP_STEPS, FEATURE_STEPS, DAY_IN_LIFE_STEP } from './DemoTourSteps-pending';

// ── Audio playback helpers ────────────────────────────────────────────────────
const AUDIO_BASE = 'https://app.flowrcmdental.com/audio';

let _currentAudio = null;

const speak = (audioFile, onEnded) => {
  stopSpeaking();
  const url = `${AUDIO_BASE}/${audioFile}?v=10`;
  _currentAudio = new Audio(url);
  if (onEnded) _currentAudio.addEventListener('ended', onEnded, { once: true });
  _currentAudio.play().catch(() => {});
};

const pauseSpeaking = () => {
  if (_currentAudio && !_currentAudio.paused) {
    _currentAudio.pause();
  }
};

const resumeSpeaking = () => {
  if (_currentAudio && _currentAudio.paused) {
    _currentAudio.play().catch(() => {});
  }
};

const stopSpeaking = () => {
  if (_currentAudio) {
    _currentAudio.pause();
    _currentAudio.currentTime = 0;
    _currentAudio = null;
  }
};

const TOUR_STEPS = [
  // ── Act 1: Setup — How to go live ────────────────────────────────────────────
  ...SETUP_STEPS,
  // ── Act 2: Watch It Work: One Claim, Start to Finish ─────────────────────────
  { ...DAY_IN_LIFE_STEP, duration: 25000 },
  {
    id: 'workflow-morning',
    route: '/',
    target: '[data-tour="claim-robot-stats"]',
    title: '— Now Watch It Work —',
    description: "It's 8 AM and you haven't touched anything yet. FlowRCM's AI worked through the night — seven claims scrubbed and corrected before submission, two aging claims flagged before they hit the 90-day deadline, three appeal letters filed automatically the moment denials came back from the payer. When payers deny claims, FlowRCM fights back without anyone asking it to. Not a single manual action was needed. This is what your revenue cycle looks like when it runs on autopilot.",
    duration: 70000,
    audioFile: 'workflow-01.mp3',
  },
  {
    id: 'workflow-preauth',
    route: '/preauth',
    target: '[data-tour="sarah-preauth-detail"]',
    title: 'Prior Auth — Approved Before Treatment',
    description: "Before Sarah ever sat in the chair, FlowRCM checked MetLife's requirements for D2750 — porcelain crown. MetLife requires prior authorization for this procedure. FlowRCM submitted the request automatically and received approval — auth number ML-AUTH-334812 — five days before the appointment. The claim that comes in next already has this authorization attached. This is how FlowRCM prevents the most common type of denial before it ever happens.",
    duration: 68000,
    audioFile: 'workflow-preauth.mp3',
  },
  {
    id: 'workflow-new-claim',
    route: '/claims',
    target: '[data-tour="sarah-claim-row"]',
    title: 'New Claim Arrives from Open Dental',
    description: "A new claim just came in from Open Dental — procedure D2750, porcelain crown, $890, submitted to MetLife for Sarah Mitchell. In a traditional workflow, someone manually enters this into a clearinghouse portal. Here, FlowRCM detected it the moment it synced from Open Dental and started working on it immediately. Before this claim goes anywhere, the AI checks every code.",
    duration: 70000,
    audioFile: 'workflow-02.mp3',
  },
  {
    id: 'workflow-scrub',
    route: '/claims',
    target: '[data-tour="claim-coding"]',
    title: 'AI Scrubs the Claim Before It Leaves',
    description: "The AI checked D2750 — porcelain-fused-to-noble-metal crown — against MetLife's coverage rules for Sarah's plan. Code is correct, 94% confidence. MetLife requires pre-treatment X-rays, tooth surface notation, and clinical notes attached to the claim for D2750. So the AI pulled all three from Sarah's Open Dental record and attached them directly to the claim form. It also confirmed the prior authorization number obtained five days earlier is properly referenced. Every box MetLife's adjudication system checks is covered. The claim is complete and ready to go out.",
    duration: 72000,
    audioFile: 'workflow-03.mp3',
  },
  {
    id: 'workflow-submitted',
    route: '/claims',
    target: '[data-tour="claim-detail"]',
    title: 'Submitted Automatically — No Portal Login',
    description: "The claim went out as an EDI 837D file through your Office Ally clearinghouse connection — complete, with all documentation attached. The prior auth is on it. The X-rays are attached. The surface notation is there. No one logged into a portal. No one uploaded a file. No one clicked submit. The AI did the prep work, confirmed everything was in order, and sent it automatically. This is what claim submission looks like when it's done right.",
    duration: 70000,
    audioFile: 'workflow-04.mp3',
  },
  {
    id: 'workflow-payment',
    route: '/era-posting',
    target: '[data-tour="sarah-era-payment"]',
    title: 'Paid — $778 Posted Automatically',
    description: "Fourteen days later, MetLife approved the claim and sent payment — first time, no denial, no appeal needed. The ERA file arrived at Office Ally, FlowRCM detected it, matched it to Sarah Mitchell's claim, and posted $778 automatically. Then — without any human action — FlowRCM detected that Sarah owes $112 in patient responsibility and immediately sent her a payment link by email. She clicks it, pays, and the money goes directly to your practice's bank account. No statement to mail, no portal for her to log into, no phone call from your front desk. The prior auth, the clean coding, the documentation attached at submission — that is why this claim paid on the first submission. That is the point.",
    duration: 70000,
    audioFile: 'workflow-07.mp3',
  },
  {
    id: 'workflow-patient-balance',
    route: '/patients',
    target: '[data-tour="sarah-balance-card"]',
    title: 'Patient Balance — Handled Automatically',
    description: "Insurance paid their portion. Sarah still has $112 outstanding — her patient responsibility. FlowRCM already sent her a payment link the moment the ERA posted. She clicks it and pays — card, bank transfer, HSA, FSA, whatever she has. No statement to mail, no portal for her to log into, no phone call from your front desk. The payment posts automatically and the balance clears.",
    duration: 40000,
    audioFile: 'workflow-08.mp3',
  },
  {
    id: 'workflow-financial-ai',
    route: '/patients',
    target: '[data-tour="financial-ai"]',
    scrollBlock: 'start',
    title: 'Patient Financial AI — Right Strategy, Automatically',
    description: "This is what the Patient Financial AI produced for Sarah. Collection likelihood: 87 percent. The AI already sent her a payment link the moment MetLife paid — and it drafted the follow-up message you see here, ready to send. If she doesn't respond within seven days, a follow-up goes out automatically. Your front desk made zero decisions on this account. The AI scored her, picked the right approach, sent the link, and scheduled the follow-up. If she pays, the balance clears. If she doesn't respond, the AI escalates — certified letter, hardship review, final notice — all on a schedule without anyone managing it.",
    duration: 75000,
    audioFile: 'workflow-09.mp3',
  },
  // ── Feature Tour: Every Section of the Platform ──────────────────────────────
  {
    id: 'claim-robot-header',
    route: '/',
    target: '[data-tour="claim-robot-header"]',
    title: '① The AI Claim Robot — Always On',
    description: "FlowRCM's AI Claim Robot is the engine behind your entire revenue cycle — and it never stops running. The moment a claim enters the system, the AI takes over: scrubbing for coding errors before submission, verifying patient eligibility in real time, generating appeal letters the moment a denial comes back, and flagging aging claims before they hit 90 days and become uncollectable. Your billing team used to spend 60 to 80 percent of their time on these repetitive tasks. With FlowRCM, that time drops to near zero — they focus only on exceptions that genuinely need a human decision.",
    duration: 75000,
    audioFile: 'step-01.mp3',
  },
  {
    id: 'claim-robot-stats',
    route: '/',
    target: '[data-tour="claim-robot-stats"]',
    title: '② What the AI Did Today',
    description: "Here is exactly what the AI did today — before your billing team logged in. Three appeal letters were filed automatically the moment denials came back from the payer — no one had to notice the denial, draft the letter, or submit it manually. Two aging claims were flagged before they hit the 90-day mark, when payers commonly stop accepting appeals. Seven claims were scrubbed and corrected before submission, preventing denials before they happen. And four payment reminders went out to patients with outstanding balances. Every single one of these tasks is something your billing team was doing by hand.",
    duration: 75000,
    audioFile: 'step-02.mp3',
  },
  {
    id: 'claim-robot-stream',
    route: '/',
    target: '[data-tour="claim-robot-stream"]',
    title: '③ Every Action Logged in Real Time',
    description: "Every action the Claim Robot takes is logged here in real time — the patient name, the payer, the exact dollar amount, and the precise timestamp. If a doctor asks why a claim was appealed, or if an audit ever requires you to show what happened with a specific patient's billing, the answer is right here in seconds. This is a complete audit trail that runs automatically in the background. Most practices either don't have this at all, or are piecing it together manually from emails and portal screenshots. FlowRCM generates it automatically on every single action.",
    duration: 75000,
    audioFile: 'step-03.mp3',
  },
  {
    id: 'denied-claims',
    route: '/claims',
    target: '[data-tour="denial-info"]',
    title: '④ Denials Caught the Moment They Happen',
    description: "When a payer denies a claim, FlowRCM flags it instantly — the moment the denial code comes back through the ERA file, the claim status updates and the AI begins building the appeal. In a traditional billing workflow, denials sit unnoticed for days or even weeks until someone manually checks the clearinghouse portal. By then, you have already lost time on the appeal window. FlowRCM eliminates that delay entirely. The denied claims flagged here were caught automatically, with appeal letters already queued — before your billing team even saw the notification.",
    duration: 75000,
    audioFile: 'step-05.mp3',
  },
  {
    id: 'appeal-button',
    route: '/claims',
    target: '[data-tour="appeal-section"]',
    title: '⑤ AI-Written Appeal Letter — Ready to Send',
    description: "This is a complete, payer-specific appeal letter written entirely by the AI — the correct denial reason code, a full clinical justification tailored to this specific procedure and payer, a supporting documentation checklist, and step-by-step resubmission instructions. Writing this manually takes a billing specialist 20 to 30 minutes per claim. FlowRCM produces it in seconds. Your team reviews it, approves it, and submits it. The AI does the research, the drafting, and the formatting — your team makes the final call.",
    duration: 80000,
    audioFile: 'step-06.mp3',
  },
  {
    id: 'appeal-approved',
    route: '/claims',
    target: '[data-tour="claim-detail"]',
    title: 'Appeal Won — $420 Posted',
    description: "Three weeks after Delta Dental denied that claim, the appeal came back approved. James Torres's root canal — $420 paid in full. No phone calls. No arguing with an insurance rep. No resubmission by hand. FlowRCM filed the appeal the moment the denial came in, with the correct documentation, to the correct address. The payment posted the moment the approval came back through the ERA. That is what a won appeal looks like. Most practices either never file appeals or file them too late and lose the window. FlowRCM files every one, immediately, and this is the result.",
    duration: 60000,
    audioFile: 'appeal-approved.mp3',
  },
  {
    id: 'claim-coding',
    route: '/claims',
    target: '[data-tour="claim-coding"]',
    title: '⑥ AI Coding — No More Guessing',
    description: "Before a claim leaves your practice, FlowRCM verifies every CDT code against the payer's specific coverage rules. D3330 — molar root canal — is showing 98 percent confidence with a low denial risk flag, meaning this code is clean and ready to submit. D2950, a core buildup, has been verified as a valid add-on against this payer's bundling rules. Miscoding is responsible for a significant portion of all preventable dental claim denials. FlowRCM catches these errors at the source — before the claim ever reaches the clearinghouse — so they never become denials in the first place.",
    duration: 80000,
    audioFile: 'step-07.mp3',
  },
  {
    id: 'preauth',
    route: '/preauth',
    target: '[data-tour="preauth-content"]',
    title: '⑦ Prior Auth — Before Denials Even Happen',
    description: "FlowRCM checks authorization requirements for every procedure before treatment begins and generates prior auth requests automatically when required. Every authorization is tracked here with its current status — approved, pending, or denied — along with the exact decision date and auth number so your team always has what they need. When an authorization comes back denied, FlowRCM includes specific resubmission guidance so your billing team knows exactly what documentation to add and which field to correct before rescheduling the patient. Preventing denials before treatment is always faster and cheaper than appealing them after.",
    duration: 80000,
    audioFile: 'step-08.mp3',
  },
  {
    id: 'era-posting',
    route: '/era-posting',
    target: '[data-tour="sarah-era-payment"]',
    title: '⑧ Insurance Pays — Posted Automatically',
    description: "When a payer sends payment, they include an ERA — an Electronic Remittance Advice — that explains exactly what was paid, what was adjusted, and what the patient owes. FlowRCM detects those ERA files the moment they arrive at the clearinghouse, matches each line item to the correct claim, and posts the payment automatically. $778 posted with zero manual work. Then FlowRCM checks for patient responsibility: Sarah owes $112. The moment the ERA posts, FlowRCM automatically sends Sarah a payment link by email — she clicks, pays, and the balance clears. No statement, no portal login, no manual entry. ERA posting used to take your billing team two to three hours every morning. FlowRCM does it the moment the file arrives, then handles patient collection automatically.",
    duration: 85000,
    audioFile: 'step-09.mp3',
  },
  {
    id: 'performance-kpis',
    route: '/performance',
    target: '[data-tour="performance-kpis"]',
    title: '⑨ Performance Dashboard — The Bottom Line',
    description: "Your collection rate, denial rate, average days to payment, and total revenue collected — all tracked automatically and updated in real time as claims move through the system. There are no spreadsheets to maintain, no manual calculations at end of month, and no waiting for someone to pull a report before you know where your practice stands. Billing companies use this dashboard to monitor the financial health of every practice they manage from a single screen. If a practice's denial rate is climbing or days to payment is increasing, it shows up here immediately — before it becomes a revenue problem.",
    duration: 80000,
    audioFile: 'step-10.mp3',
  },
  {
    id: 'performance-chart',
    route: '/performance',
    target: '[data-tour="performance-chart"]',
    title: '⑩ Revenue Trends — Month Over Month',
    description: "Six months of revenue billed versus revenue collected, charted automatically without anyone building a report. The gap between what is billed and what is collected is where your lost revenue lives — uncollected patient balances, written-off denials, aging claims that expired before anyone followed up. You can see exactly when that gap started closing as FlowRCM began catching more denials, filing more appeals, and scrubbing more claims before submission. This is the chart that used to take your office manager or billing company hours to build at month end. FlowRCM generates it continuously so you always have an accurate, current picture.",
    duration: 85000,
    audioFile: 'step-11.mp3',
  },
  {
    id: 'patient-list',
    route: '/patients',
    target: '[data-tour="sarah-balance-card"]',
    title: '⑪ Patient Balances — What Insurance Didn\'t Cover',
    description: "Sarah still has an outstanding balance — $112. FlowRCM already sent her a payment link the moment the MetLife ERA posted, eleven days ago. She has not paid yet. This is where most practices lose money: they send one generic statement, wait 30 days, send another, and eventually write off a large portion of patient balances as uncollectable. FlowRCM does something different. It scores every patient's likelihood to pay — based on their payment history, balance size, and how long it's been — and recommends the right next move for each patient individually. Let's look at what it recommends for Sarah.",
    duration: 85000,
    audioFile: 'step-12.mp3',
  },
  {
    id: 'financial-ai',
    route: '/patients',
    target: '[data-tour="financial-ai"]',
    title: '⑫ Patient Financial AI — Know How to Collect',
    description: "Sarah is at 87 percent collection likelihood — the AI recommends a single follow-up with the same payment link. The original link went out 11 days ago. She is likely to pay with one more touch. FlowRCM has already drafted the follow-up message — personalized to Sarah, her balance, and the specific claim. Your front desk clicks send. That is the entire workflow. For patients with lower collection likelihood, FlowRCM recommends a different approach — a phone call, a payment plan offer, or a different timing. The AI decides this for every patient so your team does not have to. When Sarah pays, the $112 posts to her account automatically. No manual entry, no reconciliation, no follow-up needed.",
    duration: 85000,
    audioFile: 'step-13.mp3',
  },
  {
    id: 'open-dental-sync',
    route: '/settings?tab=pms',
    target: '[data-tour="pms-integration"]',
    title: '⑬ PMS Integration — Connect Once, Sync Forever',
    description: "FlowRCM connects to every major practice management system — WaveOrtho, Open Dental, Eaglesoft, Dentrix Ascend, and Carestream. For WaveOrtho, Eaglesoft, Dentrix Ascend, and Carestream, enter your portal credentials here and FlowRCM pulls your data automatically every night. For Open Dental, FlowRCM goes deeper — it connects directly through the Open Dental REST API using your Customer Key, syncing patients, claims, carriers, and insurance plans in real time. No CSV exports, no manual uploads, no data entry. Every new patient, every new claim, every treatment plan update flows into FlowRCM automatically. For billing companies and DSOs managing multiple locations, each practice gets its own independent connection.",
    duration: 80000,
    audioFile: 'step-14.mp3',
  },
  {
    id: 'clearinghouse-config',
    route: '/settings?tab=clearinghouse',
    target: '[data-tour="clearinghouse-settings"]',
    title: '⑭ Clearinghouse — Full EDI Automation',
    description: "Enter your SFTP credentials once for Office Ally, Renaissance, or any supported clearinghouse, and FlowRCM takes over the entire EDI cycle from that point forward. Claims go out automatically as 837D files the moment they are ready. ERA payment files come back from the payer and post themselves without anyone touching them. Pre-authorization requests push out and responses sync back, updating claim status automatically. Your team stops logging into clearinghouse portals entirely and focuses only on the exceptions that genuinely require a human decision. This is the complete automation loop that FlowRCM delivers from day one.",
    duration: 85000,
    audioFile: 'step-15.mp3',
  },
  // ── Act 3 additions: Multi-location, document attachments, onboarding summary ─
  ...FEATURE_STEPS,
];

const DemoTourContext = createContext(null);

export const DemoTourProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showEndScreen, setShowEndScreen] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const timerRef = useRef(null);
  const navigateRef = useRef(null);
  const stepStartRef = useRef(null);
  const remainingRef = useRef(null);
  const voiceEnabledRef = useRef(true);

  useEffect(() => { voiceEnabledRef.current = voiceEnabled; }, [voiceEnabled]);

  const setNavigate = useCallback((nav) => {
    navigateRef.current = nav;
  }, []);


  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const goToStep = useCallback((index, duration = null) => {
    clearTimer();
    setIsPaused(false);
    remainingRef.current = null;
    stepStartRef.current = null;

    if (index >= TOUR_STEPS.length) {
      stopSpeaking();
      setShowEndScreen(true);
      return;
    }
    const step = TOUR_STEPS[index];
    const stepDuration = duration !== null ? duration : step.duration;
    setCurrentStepIndex(index);
    stepStartRef.current = Date.now();
    if (navigateRef.current) {
      navigateRef.current(step.route);
    }
    setTimeout(() => {
      if (voiceEnabledRef.current && step.audioFile) {
        speak(step.audioFile, () => {
          // Audio finished — advance after 2s pause
          clearTimer();
          timerRef.current = setTimeout(() => goToStep(index + 1), 2000);
        });
      }
    }, 800);
    // Fallback: advance after full duration if audio fails or voice is off
    timerRef.current = setTimeout(() => {
      stopSpeaking();
      goToStep(index + 1);
    }, stepDuration);
  }, [clearTimer]);

  const startTour = useCallback(() => {
    setIsActive(true);
    setShowEndScreen(false);
    setIsPaused(false);
    setCurrentStepIndex(0);
    goToStep(0);
  }, [goToStep]);

  const endTour = useCallback(() => {
    clearTimer();
    stopSpeaking();
    setIsActive(false);
    setShowEndScreen(false);
    setIsPaused(false);
    setCurrentStepIndex(0);
    remainingRef.current = null;
    stepStartRef.current = null;
  }, [clearTimer]);

  const pauseTour = useCallback(() => {
    if (!isActive || isPaused) return;
    clearTimer();
    pauseSpeaking();
    setIsPaused(true);
    const step = TOUR_STEPS[currentStepIndex];
    const elapsed = stepStartRef.current ? Date.now() - stepStartRef.current : 0;
    remainingRef.current = Math.max(1000, step.duration - elapsed);
  }, [isActive, isPaused, currentStepIndex, clearTimer]);

  const resumeTour = useCallback(() => {
    if (!isActive || !isPaused) return;
    setIsPaused(false);
    if (voiceEnabledRef.current) resumeSpeaking();
    const remaining = remainingRef.current || TOUR_STEPS[currentStepIndex].duration;
    remainingRef.current = null;
    stepStartRef.current = Date.now() - (TOUR_STEPS[currentStepIndex].duration - remaining);
    timerRef.current = setTimeout(() => {
      goToStep(currentStepIndex + 1);
    }, remaining);
  }, [isActive, isPaused, currentStepIndex, goToStep]);

  const nextStep = useCallback(() => {
    goToStep(currentStepIndex + 1);
  }, [currentStepIndex, goToStep]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      goToStep(currentStepIndex - 1);
    }
  }, [currentStepIndex, goToStep]);

  return (
    <DemoTourContext.Provider value={{
      isActive,
      isPaused,
      currentStep: TOUR_STEPS[currentStepIndex],
      currentStepIndex,
      totalSteps: TOUR_STEPS.length,
      showEndScreen,
      voiceEnabled,
      setVoiceEnabled,
      startTour,
      endTour,
      pauseTour,
      resumeTour,
      nextStep,
      prevStep,
      setNavigate,
    }}>
      {children}
    </DemoTourContext.Provider>
  );
};

export const useDemoTour = () => useContext(DemoTourContext);
export default DemoTourContext;
